import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    LinearProgress,
    Paper
} from '@mui/material';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PlaylistRemoveRoundedIcon from '@mui/icons-material/PlaylistRemoveRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { DataGrid } from '@mui/x-data-grid';

import BiMetric from '../../components/biMetric';
import PageHeader from '../../components/pageHeader';
import { csvDecimal, exportRowsToCsv } from '../../utils/csvExport';
import {
    formatBackendDatePtBr,
    parseBackendDate
} from '../../utils/dateUtils';
import { formatInteger } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    buildBaixaveisRows,
    buildNegativaveisRows,
    fetchBaixaveis,
    fetchNegativaveis,
    sumTitulos
} from './listaSerasaData';
import '../css/biAluguelAccess.css';
import '../css/biListaSerasa.css';

const emptyNegativaveisLabel = 'Nenhum título negativável encontrado.';
const emptyBaixaveisLabel = 'Nenhum título baixável encontrado.';

const ListaSerasaTable = ({
    columns,
    csvBaseName,
    emptyLabel,
    eyebrow,
    icon: Icon,
    initialSortField,
    isLoading,
    rows,
    title
}) => (
    <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel bi-ge-lista-serasa-table-panel'>
        <div className='bi-aluguel-access-panel-heading'>
            <div>
                <p>{eyebrow}</p>
                <h2>{title}</h2>
            </div>

            <div className='bi-ge-lista-serasa-panel-actions'>
                <Button
                    className='bi-ge-lista-serasa-export-button'
                    variant='outlined'
                    startIcon={<DownloadRoundedIcon />}
                    disabled={isLoading || rows.length === 0}
                    onClick={() => exportRowsToCsv({ baseName: csvBaseName, columns, rows })}
                >
                    Exportar CSV
                </Button>
                <Icon />
            </div>
        </div>

        <div className='bi-aluguel-access-grid-shell bi-ge-lista-serasa-grid-shell'>
            <DataGrid
                className='bi-aluguel-access-grid'
                rows={rows}
                columns={columns}
                loading={isLoading}
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: emptyLabel }}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 25
                        }
                    },
                    sorting: {
                        sortModel: [{ field: initialSortField, sort: 'desc' }]
                    }
                }}
            />
        </div>
    </Paper>
);

const ListaSerasaBIGE = () => {
    const [negativaveis, setNegativaveis] = useState([]);
    const [baixaveis, setBaixaveis] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadListaSerasa = useCallback(async () => {
        setIsLoading(true);
        const [titulosNegativaveis, titulosBaixaveis] = await Promise.all([
            fetchNegativaveis(),
            fetchBaixaveis()
        ]);
        setNegativaveis(titulosNegativaveis);
        setBaixaveis(titulosBaixaveis);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadListaSerasa();
    }, [loadListaSerasa]);

    const negativaveisRows = useMemo(() => buildNegativaveisRows(negativaveis), [negativaveis]);
    const baixaveisRows = useMemo(() => buildBaixaveisRows(baixaveis), [baixaveis]);
    const valorNegativaveis = useMemo(() => sumTitulos(negativaveis), [negativaveis]);
    const valorBaixaveis = useMemo(() => sumTitulos(baixaveis), [baixaveis]);

    const negativaveisColumns = useMemo(() => [
        {
            field: 'documento',
            headerName: 'Documento',
            minWidth: 150,
            flex: 0.7
        },
        {
            field: 'razao_social',
            headerName: 'Razão Social',
            minWidth: 260,
            flex: 1.35
        },
        {
            field: 'valor',
            headerName: 'Valor',
            type: 'number',
            minWidth: 145,
            flex: 0.55,
            valueFormatter: (value) => formatCurrency(value),
            csvFormatter: csvDecimal
        },
        {
            field: 'vencimento',
            headerName: 'Vencimento',
            minWidth: 145,
            flex: 0.55,
            valueGetter: (value) => parseBackendDate(value),
            valueFormatter: (value) => formatBackendDatePtBr(value)
        },
        {
            field: 'codigo_lancamento',
            headerName: 'Código de Lançamento',
            minWidth: 190,
            flex: 0.7,
            align: 'center',
            headerAlign: 'center'
        }
    ], []);

    const baixaveisColumns = useMemo(() => [
        {
            field: 'documento',
            headerName: 'Documento',
            minWidth: 150,
            flex: 0.7
        },
        {
            field: 'razao_social',
            headerName: 'Razão Social',
            minWidth: 260,
            flex: 1.35
        },
        {
            field: 'valor',
            headerName: 'Valor',
            type: 'number',
            minWidth: 145,
            flex: 0.55,
            valueFormatter: (value) => formatCurrency(value),
            csvFormatter: csvDecimal
        },
        {
            field: 'data_negativacao',
            headerName: 'Data de Negativação',
            minWidth: 190,
            flex: 0.7,
            valueGetter: (value) => parseBackendDate(value),
            valueFormatter: (value) => formatBackendDatePtBr(value)
        },
        {
            field: 'codigo_lancamento',
            headerName: 'Código de Lançamento',
            minWidth: 190,
            flex: 0.7,
            align: 'center',
            headerAlign: 'center'
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-lista-serasa-page'>
            <PageHeader>Lista Serasa</PageHeader>

            <section className='bi-aluguel-access-shell bi-ge-lista-serasa-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-aluguel-access-metrics bi-ge-lista-serasa-metrics'>
                    <BiMetric
                        className=''
                        icon={BlockRoundedIcon}
                        label='Títulos negativáveis'
                        value={formatInteger(negativaveis.length)}
                        subtitle={`Total: ${formatCurrency(valorNegativaveis)}`}
                        tone='blue'
                    />
                    <BiMetric
                        className=''
                        icon={PaidRoundedIcon}
                        label='Títulos baixáveis'
                        value={formatInteger(baixaveis.length)}
                        subtitle={`Total: ${formatCurrency(valorBaixaveis)}`}
                        tone='green'
                    />
                </div>

                <div className='bi-ge-lista-serasa-tables'>
                    <ListaSerasaTable
                        columns={negativaveisColumns}
                        csvBaseName='lista-serasa-negativaveis'
                        emptyLabel={emptyNegativaveisLabel}
                        eyebrow='Títulos para negativação'
                        icon={PlaylistRemoveRoundedIcon}
                        initialSortField='vencimento'
                        isLoading={isLoading}
                        rows={negativaveisRows}
                        title='Negativáveis'
                    />
                    <ListaSerasaTable
                        columns={baixaveisColumns}
                        csvBaseName='lista-serasa-baixaveis'
                        emptyLabel={emptyBaixaveisLabel}
                        eyebrow='Títulos para baixa'
                        icon={ReceiptLongRoundedIcon}
                        initialSortField='data_negativacao'
                        isLoading={isLoading}
                        rows={baixaveisRows}
                        title='Baixáveis'
                    />
                </div>
            </section>
        </Box>
    );
};

export default ListaSerasaBIGE;
